import { Injectable } from '@angular/core';
import { ClaimReport } from '../interfaces/claim-report';
import { UserType } from '../enums/user-type';
import { MultiplePartyInvolvementType } from '../enums/externals-involved-type';
import { ClaimType } from '../enums/claim-type';
import { IncidentType } from '../enums/incident-type';
import { InvolvedVehicleType } from '../enums/involved-vehicle-type';
import { VehicleIncidentStatusType } from '../enums/vehicle-incident-status-type';
import { RideReason } from '../enums/ride-reason';
import { TechnicalReasonType } from '../enums/technical-reason-type';
import { WhoIsAtFault } from '../enums/who-is-at-fault';
import { AccidentRoleType } from '../enums/accident-role-type';
import { CarPart } from '../enums/car-part';

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

type GetNestedType<T, K extends string> = K extends keyof T
  ? T[K]
  : K extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? T[Key] extends object
      ? GetNestedType<T[Key], Rest>
      : never
    : never
  : never;

@Injectable({
  providedIn: 'root'
})
export class MockingClaimsService {
  private baseMock: ClaimReport;

  constructor() {
    this.baseMock = this.createMinimalMock();
  }

  private createMinimalMock(): ClaimReport {
    return {
      userType: null,
      externalsInvolved: null,
      licensePlate: null,
      incidentAddress: {
        street: '',
        houseNumber: '',
        postcode: '',
        city: ''
      },
      incidentDate: null,
      claimType: null,
      incidentTypes: null,
      vehicleType: null,
      vehicleIncidentStatusType: null,
      involvedCarParts: [],
      detailedDescription: null,
      rideReason: null,
      age: null,
      hasConsumedDrugs: null,
      isActiveRider: null,
      policeReportWasIssued: null,
      noPoliceReportReason: null,
      policeDrugTestPerformed: null,
      witnessesAreKnown: null,
      witnesses: [],
      technicalIssue: null,
      technicalIssueExplanation: null,
      fault: null,
      thirdPartyEntities: [],
      fileUploads: null,
      policeReportImageUrls: [],
      policeReportAvailable: null,
      policeReportNotAvailableReason: null,
      personalInformation: {
        name: '',
        surname: '',
        phone: '',
        email: '',
        city: '',
        zip: 0,
        streetAndNumber: '',
        contactableByEmail: false,
        contactableBySms: false
      },
      userHasConfirmedCorrectnessOfData: null,
      isVerified: null,
      accidentRoleType: null
    };
  }

  getMockClaimReport(): ClaimReport {
    return JSON.parse(JSON.stringify(this.baseMock));
  }

  updateField<K extends NestedKeyOf<ClaimReport>>(
    fieldPath: K,
    value: GetNestedType<ClaimReport, K>
  ): MockingClaimsService {
    const keys = fieldPath.split('.') as string[];
    let current: any = this.baseMock;

    for (let i = 0; i < keys.length - 1; i++) {
      if (current[keys[i]] === null || current[keys[i]] === undefined) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    return this;
  }

  updateFields(updates: DeepPartial<ClaimReport>): MockingClaimsService {
    this.deepMerge(this.baseMock, updates);
    return this;
  }

  resetToMinimal(): MockingClaimsService {
    this.baseMock = this.createMinimalMock();
    return this;
  }

  loadStandardMock(): MockingClaimsService {
    this.baseMock = {
      userType: UserType.FirstParty,
      externalsInvolved: MultiplePartyInvolvementType.AnotherExternal,
      licensePlate: '123ABC',
      incidentAddress: {
        street: 'Hauptstraße',
        houseNumber: '123',
        postcode: '10115',
        city: 'Berlin'
      },
      incidentDate: new Date('2024-08-15T14:30:00'),
      claimType: ClaimType.Both,
      incidentTypes: [IncidentType.RearEndCollision],
      vehicleType: InvolvedVehicleType.FourWheelsWithEngine,
      vehicleIncidentStatusType: VehicleIncidentStatusType.Parked,
      involvedCarParts: [ CarPart.Front ],
      detailedDescription: 'Standard collision scenario',
      rideReason: RideReason.Commute,
      age: 32,
      hasConsumedDrugs: false,
      isActiveRider: true,
      policeReportWasIssued: true,
      noPoliceReportReason: 'reason',
      policeDrugTestPerformed: false,
      witnessesAreKnown: true,
      witnesses: [
        {
          name: 'Jane',
          surname: 'Smith',
          phone: '+49 30 12345678',
          email: 'jane.smith@example.com'
        }
      ],
      technicalIssue: TechnicalReasonType.Yes,
      technicalIssueExplanation: 'explanation',
      fault: WhoIsAtFault.ThirdParty,
      thirdPartyEntities: [
        {
          name: 'Michael',
          surname: 'Brown',
          phone: '+49 30 87654321',
          email: 'michael.brown@example.com'
        }
      ],
      fileUploads: [],
      policeReportImageUrls: [],
      policeReportAvailable: true,
      policeReportNotAvailableReason: 'no reason',
      personalInformation: {
        name: 'Max',
        surname: 'Mustermann',
        phone: '+49 30 12345678',
        email: 'max.mustermann@example.com',
        city: 'Berlin',
        zip: 10115,
        streetAndNumber: 'Musterstraße 42',
        contactableByEmail: true,
        contactableBySms: true
      },
      userHasConfirmedCorrectnessOfData: true,
      isVerified: true,
      accidentRoleType: AccidentRoleType.Driver
    };
    return this;
  }

  loadComplexMock(): MockingClaimsService {
    this.loadStandardMock();
    this.updateFields({
      incidentTypes: [
        IncidentType.ParkedVehicleCollision,
      ],
      involvedCarParts: [
        CarPart.Front, CarPart.Top, CarPart.Left,
      ],
      technicalIssueExplanation: 'Brake pedal became unresponsive',
      witnesses: [
        { name: 'Jane', surname: 'Smith', phone: '+49 30 12345678', email: 'jane.smith@example.com' },
        { name: 'Robert', surname: 'Johnson', phone: '+49 30 87654321', email: 'robert.johnson@example.com' },
        { name: 'Maria', surname: 'Garcia', phone: '+49 30 11223344', email: 'maria.garcia@example.com' }
      ]
    });
    return this;
  }

  private deepMerge(target: any, source: any): void {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key] || typeof target[key] !== 'object') {
          target[key] = {};
        }
        this.deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }


  getMockClaimReportAsJson(): string {
    return JSON.stringify(this.getMockClaimReport(), null, 2);
  }

  getMinimalMockClaimReport(): ClaimReport {
    return this.resetToMinimal().getMockClaimReport();
  }

  getComplexMockClaimReport(): ClaimReport {
    return this.loadComplexMock().getMockClaimReport();
  }
}
